import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CorporateEntityContextType, CorporateEntityProfile } from '@typeDefs/domain/profile';
import { TreasuryBillAssetSummary } from '@typeDefs/domain/assets';
import { DEFAULT_CHARGE_RATE_PER_ANNUM } from '@typeDefs/domain/entities';
import { useProfile } from '@contexts/profile/ProfileContext';
import { treasuryBillService, businessService } from '@services/api';

const BusinessContext = createContext<CorporateEntityContextType | undefined>(undefined);

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get active business from profile context; availableBusinesses carries each
  // entity's chargeRatePerAnnum (bug-75).
  const { currentBusinessId, availableBusinesses } = useProfile();

  // Core business state
  const [businessProfile, setBusinessProfile] = useState<CorporateEntityProfile | null>(null);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [allTreasuryBills, setAllTreasuryBills] = useState<TreasuryBillAssetSummary[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);

  // Counter to trigger dashboard refresh - incremented whenever business data is refreshed
  const [businessDataRefreshTrigger, setBusinessDataRefreshTrigger] = useState(0);

  // Computed values for different bill statuses
  const pendingTreasuryBillOrders = useMemo(
    () => allTreasuryBills.filter((bill) => bill.status === 'pending'),
    [allTreasuryBills],
  );

  const activeTreasuryBillOrders = useMemo(
    () => allTreasuryBills.filter((bill) => bill.status === 'active'),
    [allTreasuryBills],
  );

  const completedTreasuryBillOrders = useMemo(
    () => allTreasuryBills.filter((bill) => bill.status === 'sold' || bill.status === 'matured'),
    [allTreasuryBills],
  );

  // bug-75: the ACTIVE entity's management-fee rate in PERCENT per annum
  // (0.3 = 0.3%/yr). The mapper already resolved fallback/clamping onto every
  // CorporateEntitySummary; the default here only covers "no active entity yet".
  const chargeRatePerAnnum = useMemo(() => {
    const activeEntity = availableBusinesses.find((b) => b.id === currentBusinessId);
    return activeEntity?.chargeRatePerAnnum ?? DEFAULT_CHARGE_RATE_PER_ANNUM;
  }, [availableBusinesses, currentBusinessId]);

  // Clear all business data
  const clearBusinessData = useCallback(() => {
    setBusinessProfile(null);
    setAllTreasuryBills([]);
    setBusinessError(null);
    setAssetsError(null);
  }, []);

  const fetchBusinessProfile = useCallback(async (businessId: string) => {
    console.log('=== FETCHING BUSINESS PROFILE ===');
    console.log('Business ID:', businessId);

    setBusinessLoading(true);
    setBusinessError(null);

    try {
      const profile = await businessService.getProfile(businessId);
      console.log('Business profile fetched successfully:', profile);
      setBusinessProfile(profile);
    } catch (err) {
      console.error('Error fetching business profile:', err instanceof Error ? err.message : err);
      setBusinessError('Failed to load business profile');
    } finally {
      setBusinessLoading(false);
    }
  }, []);

  // Fetch treasury bills
  const fetchTreasuryBills = useCallback(async (businessId: string) => {
    setAssetsLoading(true);
    setAssetsError(null);
    try {
      const bills = await treasuryBillService.getAllTreasuryBills(businessId);
      setAllTreasuryBills(bills);
    } catch (err) {
      setAssetsError('Failed to load treasury bills');
      console.error('Error fetching treasury bills:', err instanceof Error ? err.message : err);
    } finally {
      setAssetsLoading(false);
    }
  }, []);

  // Public methods
  const refreshBusinessData = useCallback(
    async (businessId?: string) => {
      const targetBusinessId = businessId || currentBusinessId;

      if (!targetBusinessId) {
        clearBusinessData();
        return;
      }
      await fetchBusinessProfile(targetBusinessId);

      // Trigger dashboard refresh by incrementing the counter
      setBusinessDataRefreshTrigger((prev) => prev + 1);
    },
    [currentBusinessId, fetchBusinessProfile, clearBusinessData],
  );

  const refreshTreasuryBills = useCallback(async () => {
    if (!currentBusinessId) {
      setAllTreasuryBills([]);
      return;
    }
    await fetchTreasuryBills(currentBusinessId);
  }, [currentBusinessId, fetchTreasuryBills]);

  // Centralised post-mutation invalidation. After a sale/action (or purchase)
  // succeeds, refresh BOTH the My Assets list AND the profile so the holdings
  // and balance stay in sync with no manual page refresh (#222 / S10). This is
  // on-mutation-success only — no polling, no window-focus refetch, no timer.
  // The two refetches run concurrently since neither depends on the other.
  const refreshAfterMutation = useCallback(async () => {
    await Promise.all([refreshTreasuryBills(), refreshBusinessData()]);
  }, [refreshTreasuryBills, refreshBusinessData]);

  // Effect to handle business changes from user context
  useEffect(() => {
    console.log('=== BUSINESS CONTEXT EFFECT ===');
    console.log('Current Business ID:', currentBusinessId);

    if (currentBusinessId) {
      // Fetch both business profile and treasury bills when business changes
      console.log('Fetching business data for ID:', currentBusinessId);
      fetchBusinessProfile(currentBusinessId);
      fetchTreasuryBills(currentBusinessId);
    } else {
      // Clear data when no business is selected
      console.log('No business ID - clearing data');
      clearBusinessData();
    }
  }, [currentBusinessId, fetchBusinessProfile, fetchTreasuryBills, clearBusinessData]);

  const contextValue = useMemo<CorporateEntityContextType>(
    () => ({
      businessProfile,
      allTreasuryBills,
      pendingTreasuryBillOrders,
      completedTreasuryBillOrders,
      activeTreasuryBillOrders,
      chargeRatePerAnnum,
      assetsLoading,
      assetsError,
      businessLoading,
      businessError,
      businessDataRefreshTrigger,
      refreshBusinessData,
      refreshTreasuryBills,
      refreshAfterMutation,
      clearBusinessData,
    }),
    [
      businessProfile,
      allTreasuryBills,
      pendingTreasuryBillOrders,
      completedTreasuryBillOrders,
      activeTreasuryBillOrders,
      chargeRatePerAnnum,
      assetsLoading,
      assetsError,
      businessLoading,
      businessError,
      businessDataRefreshTrigger,
      refreshBusinessData,
      refreshTreasuryBills,
      refreshAfterMutation,
      clearBusinessData,
    ],
  );

  return <BusinessContext.Provider value={contextValue}>{children}</BusinessContext.Provider>;
};

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};
